/**
 * `src/lib/content/loader.ts` — the corpus access layer. SPEC §1.2, §1.3.
 *
 * Everything this app ever shows of the Qur'an is read through here.
 *
 * OFFLINE IS THE DEFAULT, NOT A FALLBACK
 * ──────────────────────────────────────
 * SPEC §1.3: all thirty juz files and `quran/meta.json` are in the service
 * worker's precache, revisioned by content hash. So the only I/O this module
 * does is a same-origin `fetch` of a static path, which the service worker
 * answers from the cache. There is no online path, no network detection, no
 * retry-with-backoff and no "you appear to be offline" state — a reader who has
 * opened the app once has the whole mushaf, and a loader that behaved
 * differently when `navigator.onLine` was false would be adding a failure mode
 * the architecture does not have.
 *
 * WHY `fetch` IS INJECTED AND NOT CALLED AS A GLOBAL
 * ─────────────────────────────────────────────────
 * Three reasons, in order of how much they matter:
 *   1. SPEC §1.2 puts `privacy/netlog.ts` in front of every egress. Taking the
 *      fetcher as a parameter is what lets the audited wrapper be dropped in
 *      later without touching this file (and is why `eslint.config.js` bans the
 *      bare `fetch` global in the first place).
 *   2. This module must run in a WORKER — `search/quran.worker.ts` indexes the
 *      same corpus — and in plain node under vitest, where a relative URL has
 *      no origin to resolve against.
 *   3. It makes the whole-corpus test a real test of this code rather than a
 *      test of a mock beside it: the spec injects a fetcher that reads the
 *      committed files off disk, and every assertion below runs on the bytes
 *      that ship.
 *
 * NO SVELTE, NO STORES, NO REACTIVITY. Plain TypeScript and promises.
 */

import { TOTAL_AYAT, ayahCountOf, ayahIdOf, isValidAyahId, isSurah, locationOf } from '../anchor';
import { assertBasmalaCensus, assertRowShape, buildAyahModel } from './model';
import {
	ContentLoadError,
	type AyahModel,
	type AyahRow,
	type JuzFile,
	type JuzMeta,
	type QuranMeta,
	type SurahMeta
} from './types';
import { assertAnchorSpace, assertCorpusVersion, assertSourceMatchesFixture } from './versions';

/** The default location the content pipeline emits to and the SW precaches. */
export const DEFAULT_BASE_PATH = '/content/quran/';

/** The 30 juz of the Hafs mushaf. */
export const JUZ_COUNT = 30;

/**
 * The minimum of `Response` this module uses. Structural on purpose: the
 * platform `fetch` satisfies it, and so does a four-line reader over `node:fs`
 * or over a `Cache` object, with no adapter in between.
 */
export interface ContentResponse {
	ok: boolean;
	status: number;
	statusText?: string;
	text(): Promise<string>;
}

/** What the loader needs from the outside world. Exactly one function. */
export type Fetcher = (url: string) => Promise<ContentResponse>;

export interface ContentLoaderOptions {
	/** Where the emitted corpus lives. Must end in `/`. */
	basePath?: string;
	/** The audited fetch wrapper, a worker's own, or a test's file reader. */
	fetch?: Fetcher;
}

/** What `verifyCorpus()` measured. Every field is checked against `meta.json`. */
export interface CorpusReport {
	ayat: number;
	words: number;
	basmalaRows: number;
	/** the 112 surahs whose ayah 1 carries a prepended basmala, ascending. */
	basmalaSurahs: number[];
	maxWordsInAyah: number;
	/** `"2:282"` under `seg-1`. */
	maxWordsAt: string;
	corpus_version: string;
	anchor_space_version: string;
	segment_version: string;
}

/**
 * The default fetcher: the platform's, reached through `globalThis`.
 *
 * Not written as a bare `fetch(...)` call — see the header, and
 * `eslint.config.js`'s `no-restricted-globals` entry, which is there so that
 * every egress in this app is visible to `privacy/netlog.ts`. Resolved on each
 * call rather than captured at module scope so a host that installs its wrapper
 * after import still gets used.
 */
function platformFetch(url: string): Promise<ContentResponse> {
	const impl = (globalThis as { fetch?: Fetcher }).fetch;
	if (typeof impl !== 'function') {
		throw new ContentLoadError(
			'fetch_failed',
			url,
			'no fetch implementation is available; pass one to createContentLoader()'
		);
	}
	return impl(url);
}

/**
 * A one-flight promise cache: concurrent callers for the same key share one
 * request, and a REJECTION is not retained.
 *
 * Retaining a rejection is the bug worth naming: the first page turn after a
 * cold start can race the service worker's activation, and a loader that cached
 * that one failure would show an empty mushaf until the tab was reloaded, on a
 * device that has the entire Qur'an on disk.
 */
class Once<K, V> {
	private readonly done = new Map<K, V>();
	private readonly flight = new Map<K, Promise<V>>();

	get(key: K, make: () => Promise<V>): Promise<V> {
		const settled = this.done.get(key);
		if (settled !== undefined) return Promise.resolve(settled);
		const inFlight = this.flight.get(key);
		if (inFlight) return inFlight;
		const promise = make()
			.then((value) => {
				this.done.set(key, value);
				this.flight.delete(key);
				return value;
			})
			.catch((error: unknown) => {
				this.flight.delete(key);
				throw error;
			});
		this.flight.set(key, promise);
		return promise;
	}

	/** Drop one settled entry. An in-flight request is deliberately left alone:
	 *  its `.then` would re-populate `done` anyway, and cancelling a fetch
	 *  somebody is awaiting is not eviction, it is a failure. */
	forget(key: K): void {
		this.done.delete(key);
	}

	clear(): void {
		this.done.clear();
		this.flight.clear();
	}
}

/** The corpus access layer. Create one per process; it is a cache. */
export interface ContentLoader {
	/** `quran/meta.json` — surah table, juz ranges, sajda positions. */
	meta(): Promise<QuranMeta>;
	/** The 114-row surah table. */
	surahTable(): Promise<readonly SurahMeta[]>;
	/** One juz file, version-checked. */
	juz(juz: number): Promise<JuzFile>;
	/** Every ayah of a juz, as render models, in order. */
	juzModels(juz: number): Promise<readonly AyahModel[]>;
	/** Every row of a surah, in order. May span several juz files. */
	surahRows(surah: number): Promise<readonly AyahRow[]>;
	/** Every ayah of a surah, as render models, in order. */
	surahModels(surah: number): Promise<readonly AyahModel[]>;
	/** One row, by `ayahId`. */
	row(ayahId: number): Promise<AyahRow>;
	/** One render model, by `ayahId`. THE word-span contract's entry point. */
	ayah(ayahId: number): Promise<AyahModel>;
	/** One render model, by surah and ayah. */
	at(surah: number, ayah: number): Promise<AyahModel>;
	/** A consecutive run of ayat, inclusive on both ends — for range anchors. */
	ayahRange(startAyahId: number, endAyahId: number): Promise<readonly AyahModel[]>;
	/** Which juz an `ayahId` falls in. */
	juzOf(ayahId: number): Promise<number>;
	/** Walk all 6,236 ayat, asserting every invariant. Returns what it counted. */
	verifyCorpus(): Promise<CorpusReport>;
	/**
	 * Which juz files are decoded right now, oldest first.
	 *
	 * Exposed so the cache BOUND can be asserted rather than described — an
	 * unbounded cache and a bounded one behave identically until the device runs
	 * out of memory, which is not a condition a test can wait for.
	 */
	residentJuz(): number[];
	/** Drop every cached file and model. */
	clear(): void;
}

/**
 * Build a loader.
 *
 * Nothing is fetched until something is asked for, and `meta.json` is fetched
 * before any juz file: the juz files' paths and the `corpus_version` every one
 * of them is checked against both come from it. That ordering is deliberate —
 * hard-coding `uthmani/juz-NN.json` here would mean the emitted manifest and the
 * reader could disagree about which files exist, with nothing to notice.
 */
export function createContentLoader(options: ContentLoaderOptions = {}): ContentLoader {
	const basePath = options.basePath ?? DEFAULT_BASE_PATH;
	const doFetch = options.fetch ?? platformFetch;

	const files = new Once<string, unknown>();
	const juzFiles = new Once<number, JuzFile>();
	let metaPromise: Promise<QuranMeta> | null = null;
	const models = new Map<number, AyahModel>();
	/** ayahId → its row, populated whenever a juz file is decoded. */
	const rows = new Map<number, AyahRow>();

	/**
	 * ═══════════════════════════════════════════════════════════════════════
	 *  THE CACHES ARE BOUNDED. THEY USED NOT TO BE, AND THAT WAS THE REAL LEAK.
	 * ═══════════════════════════════════════════════════════════════════════
	 * `files`, `juzFiles`, `rows` and `models` had no eviction at all, behind a
	 * module-level singleton (`contentLoader()`) whose `clear()` nothing calls.
	 * A reader who works through the mushaf in one session therefore accumulated
	 * all 30 parsed juz files PLUS 6,236 `AyahModel`s, each holding a
	 * `RenderSegment[]` — 77,429 segment objects in total, every one carrying
	 * `text`, `start`, `end`, `kind`, and for words `index` and `pos`.
	 *
	 * That is the allocation shape that gets a tab killed on a 2 GB Android: it
	 * grows monotonically with READING, so it is not a leak anyone would see in a
	 * short session and it is worst for the user who uses the app most.
	 *
	 * THE UNIT IS A JUZ, NOT AN AYAH, and that is the whole design. Evicting a
	 * single ayah would not free its row: the row is one element of a parsed juz
	 * file the loader is still holding, so an ayah-granular LRU would bound the
	 * wrong thing and pay for the privilege. Three resident juz is the one being
	 * read plus a neighbour either side; al-Baqarah spans three on its own, so no
	 * single surah can evict a file it is still reading.
	 *
	 * Eviction is safe because everything here is DERIVABLE: models are immutable
	 * and anything still holding a reference keeps working, it simply stops
	 * sharing the cache. The only cost of evicting too eagerly is re-parsing,
	 * measured at ~10 ms for the three files al-Baqarah needs.
	 *
	 * It is least-recently-LOADED rather than least-recently-USED: a cache hit
	 * does not reorder, because `Once.get` short-circuits before the factory
	 * runs. With a bound of three and a maximum surah span of three, the two
	 * orders cannot differ in a way a reader can reach.
	 */
	const MAX_RESIDENT_JUZ = 3;
	const resident: JuzMeta[] = [];

	function evictOldJuz(): void {
		while (resident.length > MAX_RESIDENT_JUZ) {
			const entry = resident.shift();
			if (!entry) return;
			juzFiles.forget(entry.juz);
			files.forget(`${basePath}${entry.file}`);
			for (let id = entry.start_ayah_id; id <= entry.end_ayah_id; id++) {
				rows.delete(id);
				models.delete(id);
			}
		}
	}

	/** Testing seam: how many juz files are decoded right now. */
	function residentJuzNumbers(): number[] {
		return resident.map((entry) => entry.juz);
	}

	async function readJson(path: string): Promise<unknown> {
		const url = `${basePath}${path}`;
		return files.get(url, async () => {
			let response: ContentResponse;
			try {
				response = await doFetch(url);
			} catch (error) {
				throw new ContentLoadError(
					'fetch_failed',
					url,
					`could not be fetched: ${error instanceof Error ? error.message : String(error)}`
				);
			}
			if (!response.ok) {
				throw new ContentLoadError(
					'http_error',
					url,
					`HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`
				);
			}
			const body = await response.text();
			try {
				return JSON.parse(body) as unknown;
			} catch (error) {
				throw new ContentLoadError(
					'malformed_json',
					url,
					`is not valid JSON: ${error instanceof Error ? error.message : String(error)}`
				);
			}
		});
	}

	function loadMeta(): Promise<QuranMeta> {
		if (metaPromise) return metaPromise;
		metaPromise = (async () => {
			const where = `${basePath}meta.json`;
			const raw = (await readJson('meta.json')) as Partial<QuranMeta>;
			assertAnchorSpace(raw, where);
			assertSourceMatchesFixture(raw.source, where);
			if (raw.total_ayat !== TOTAL_AYAT) {
				throw new ContentLoadError(
					'malformed_shape',
					where,
					`total_ayat is ${String(raw.total_ayat)}, the anchor space has ${TOTAL_AYAT}`
				);
			}
			if (!Array.isArray(raw.juz) || raw.juz.length !== JUZ_COUNT) {
				throw new ContentLoadError(
					'malformed_shape',
					where,
					`the juz table must have ${JUZ_COUNT} rows`
				);
			}
			if (!Array.isArray(raw.surahs) || raw.surahs.length !== 114) {
				throw new ContentLoadError('malformed_shape', where, 'the surah table must have 114 rows');
			}
			return raw as QuranMeta;
		})().catch((error: unknown) => {
			/* A failed meta must not poison the loader — see `Once`. */
			metaPromise = null;
			throw error;
		});
		return metaPromise;
	}

	async function juzMetaOf(juz: number): Promise<JuzMeta> {
		const meta = await loadMeta();
		const row = meta.juz.find((entry) => entry.juz === juz);
		if (!row) {
			throw new ContentLoadError(
				'unknown_juz',
				`${basePath}meta.json`,
				`juz ${juz} is not in the juz table (1..${JUZ_COUNT})`
			);
		}
		return row;
	}

	async function loadJuz(juz: number): Promise<JuzFile> {
		return juzFiles.get(juz, async () => {
			const meta = await loadMeta();
			const entry = await juzMetaOf(juz);
			const where = `${basePath}${entry.file}`;
			const file = (await readJson(entry.file)) as Partial<JuzFile>;

			assertAnchorSpace(file, where);
			assertCorpusVersion(file, meta.corpus_version, where);
			assertSourceMatchesFixture(file.source, where);
			if (typeof file._notice !== 'string' || file._notice === '') {
				/* SPEC §1.5: "a per-juz JSON without a `_notice` field fails the
				 * build". It must also fail the READ — the Tanzil licence's notice
				 * requirement does not stop applying once the bytes are on a
				 * device, and a missing notice means these are not the emitted
				 * bytes. */
				throw new ContentLoadError(
					'malformed_shape',
					where,
					'the required upstream `_notice` is missing (SPEC §1.5)'
				);
			}
			if (file.juz !== juz) {
				throw new ContentLoadError(
					'malformed_shape',
					where,
					`declares juz ${String(file.juz)} but was fetched as juz ${juz}`
				);
			}
			if (!Array.isArray(file.ayat)) {
				throw new ContentLoadError('malformed_shape', where, '`ayat` must be an array');
			}
			if (file.ayat.length !== entry.ayat) {
				throw new ContentLoadError(
					'malformed_shape',
					where,
					`has ${file.ayat.length} ayat, the juz table says ${entry.ayat}`
				);
			}

			let expected = entry.start_ayah_id;
			for (const row of file.ayat) {
				assertRowShape(row, where);
				if (row.i !== expected) {
					throw new ContentLoadError(
						'malformed_shape',
						where,
						`ayat are not a contiguous ascending run: expected ayahId ${expected}, got ${row.i}`
					);
				}
				const place = locationOf(row.i);
				if (row.s !== place.surah || row.a !== place.ayah) {
					throw new ContentLoadError(
						'malformed_shape',
						where,
						`ayahId ${row.i} is ${place.surah}:${place.ayah} in the frozen offset table, ` +
							`the row says ${row.s}:${row.a}`
					);
				}
				rows.set(row.i, row);
				expected++;
			}
			if (expected - 1 !== entry.end_ayah_id) {
				throw new ContentLoadError(
					'malformed_shape',
					where,
					`ends at ayahId ${expected - 1}, the juz table says ${entry.end_ayah_id}`
				);
			}
			/* Resident from here, and the oldest is dropped in the same tick, so
			 * the peak is bounded rather than the average. */
			resident.push(entry);
			evictOldJuz();
			return file as JuzFile;
		});
	}

	async function juzOfAyah(ayahId: number): Promise<number> {
		const meta = await loadMeta();
		for (const entry of meta.juz) {
			if (ayahId >= entry.start_ayah_id && ayahId <= entry.end_ayah_id) return entry.juz;
		}
		throw new ContentLoadError(
			'unknown_ayah',
			`${basePath}meta.json`,
			`ayahId ${ayahId} falls in no juz`
		);
	}

	function requireAyahId(ayahId: number): void {
		if (!isValidAyahId(ayahId)) {
			throw new ContentLoadError(
				'unknown_ayah',
				basePath,
				`ayahId ${ayahId} is not in 1..${TOTAL_AYAT}`
			);
		}
	}

	async function rowOf(ayahId: number): Promise<AyahRow> {
		requireAyahId(ayahId);
		const cached = rows.get(ayahId);
		if (cached) return cached;
		await loadJuz(await juzOfAyah(ayahId));
		const row = rows.get(ayahId);
		if (!row) {
			throw new ContentLoadError(
				'unknown_ayah',
				basePath,
				`ayahId ${ayahId} was not in the juz file that claims to contain it`
			);
		}
		return row;
	}

	/** The word-span contract, memoised. The model is frozen and shareable. */
	function modelOf(row: AyahRow): AyahModel {
		const cached = models.get(row.i);
		if (cached) return cached;
		const model = buildAyahModel(row);
		models.set(row.i, model);
		return model;
	}

	async function modelAt(ayahId: number): Promise<AyahModel> {
		return modelOf(await rowOf(ayahId));
	}

	async function surahRows(surah: number): Promise<readonly AyahRow[]> {
		if (!isSurah(surah)) {
			throw new ContentLoadError('unknown_surah', basePath, `surah ${surah} is not in 1..114`);
		}
		const first = ayahIdOf(surah, 1);
		const last = first + ayahCountOf(surah) - 1;
		const out: AyahRow[] = [];
		for (let id = first; id <= last; id++) out.push(await rowOf(id));
		return out;
	}

	return {
		meta: loadMeta,

		async surahTable() {
			return (await loadMeta()).surahs;
		},

		juz: loadJuz,

		async juzModels(juz: number) {
			const file = await loadJuz(juz);
			return file.ayat.map(modelOf);
		},

		surahRows,

		async surahModels(surah: number) {
			return (await surahRows(surah)).map(modelOf);
		},

		row: rowOf,
		ayah: modelAt,

		async at(surah: number, ayah: number) {
			return modelAt(ayahIdOf(surah, ayah));
		},

		async ayahRange(startAyahId: number, endAyahId: number) {
			requireAyahId(startAyahId);
			requireAyahId(endAyahId);
			if (endAyahId < startAyahId) {
				throw new ContentLoadError(
					'unknown_ayah',
					basePath,
					`range ${startAyahId}..${endAyahId} runs backwards`
				);
			}
			const out: AyahModel[] = [];
			for (let id = startAyahId; id <= endAyahId; id++) out.push(await modelAt(id));
			return out;
		},

		juzOf: async (ayahId: number) => {
			requireAyahId(ayahId);
			return juzOfAyah(ayahId);
		},

		/**
		 * The whole-corpus walk. Every ayah is built through `buildAyahModel`,
		 * so every per-ayah invariant runs; what this adds is the four things a
		 * per-ayah check cannot see — that all 6,236 ayat are present exactly
		 * once, that exactly 112 rows carry a basmala and which surahs they are,
		 * that the canonical word total is the one the anchor space was frozen
		 * against, and that the longest ayah is where the fixture says it is.
		 *
		 * Cheap enough to run on demand: the thirty files are ~1.7 MB and
		 * already on disk. `/repair` and `/about/coverage` are its callers.
		 */
		async verifyCorpus(): Promise<CorpusReport> {
			const meta = await loadMeta();
			const where = `${basePath}* (whole corpus)`;
			const seen = new Uint8Array(TOTAL_AYAT + 1);
			const basmalaSurahs: number[] = [];
			let ayat = 0;
			let words = 0;
			let maxWordsInAyah = 0;
			let maxWordsAt = '';

			for (let juz = 1; juz <= JUZ_COUNT; juz++) {
				const file = await loadJuz(juz);
				let juzWords = 0;
				for (const row of file.ayat) {
					const model = modelOf(row);
					if (seen[model.ayahId] === 1) {
						throw new ContentLoadError(
							'malformed_shape',
							where,
							`ayahId ${model.ayahId} appears in more than one juz file`
						);
					}
					seen[model.ayahId] = 1;
					ayat++;
					words += model.wordCount;
					juzWords += model.wordCount;
					if (model.basmala) basmalaSurahs.push(model.surah);
					if (model.wordCount > maxWordsInAyah) {
						maxWordsInAyah = model.wordCount;
						maxWordsAt = `${model.surah}:${model.ayah}`;
					}
				}
				if (juzWords !== file.words) {
					throw new ContentLoadError(
						'word_count_mismatch',
						`${basePath}${(await juzMetaOf(juz)).file}`,
						`the canonical words of juz ${juz} total ${juzWords}, the file header says ${file.words}`
					);
				}
			}

			const missing: number[] = [];
			for (let id = 1; id <= TOTAL_AYAT; id++) if (seen[id] !== 1) missing.push(id);
			if (missing.length > 0) {
				throw new ContentLoadError(
					'malformed_shape',
					where,
					`${missing.length} ayat are missing from the corpus, first ${missing[0]}`
				);
			}
			if (ayat !== TOTAL_AYAT) {
				throw new ContentLoadError(
					'malformed_shape',
					where,
					`walked ${ayat} ayat, the anchor space has ${TOTAL_AYAT}`
				);
			}

			assertBasmalaCensus(basmalaSurahs, where);

			if (words !== meta.total_words) {
				throw new ContentLoadError(
					'word_count_mismatch',
					where,
					`the corpus segments to ${words} canonical words, meta.json says ${meta.total_words}`
				);
			}
			if (maxWordsInAyah !== meta.max_words_in_ayah || maxWordsAt !== meta.max_words_at) {
				throw new ContentLoadError(
					'word_count_mismatch',
					where,
					`the longest ayah is ${maxWordsAt} at ${maxWordsInAyah} words, meta.json says ` +
						`${meta.max_words_at} at ${meta.max_words_in_ayah}`
				);
			}

			basmalaSurahs.sort((a, b) => a - b);
			return {
				ayat,
				words,
				basmalaRows: basmalaSurahs.length,
				basmalaSurahs,
				maxWordsInAyah,
				maxWordsAt,
				corpus_version: meta.corpus_version,
				anchor_space_version: meta.anchor_space_version,
				segment_version: meta.segment_version
			};
		},

		residentJuz: residentJuzNumbers,

		clear() {
			files.clear();
			juzFiles.clear();
			metaPromise = null;
			models.clear();
			rows.clear();
			resident.length = 0;
		}
	};
}

/**
 * The app's loader. One per document or worker, created on first use so that
 * importing this module costs nothing and never touches the network.
 */
let shared: ContentLoader | null = null;

/** The process-wide loader over `/content/quran/`. */
export function contentLoader(): ContentLoader {
	shared ??= createContentLoader();
	return shared;
}
